import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/senca/logo.jpg";
import styles from "./Landing.module.css";
import modernStyles from "./LandingModern.module.css";
import denunciaService from "./denunciaService";
import {
  aboutImageSrc,
  aboutParagraphs,
  contactInfo,
  heroImageSrc,
  projects,
  socialLinks,
  statIconImages,
  stats,
} from "./landingContent";

const scrollToSection = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const EMPTY_DENUNCIA = {
  titulo: "",
  descricao: "",
  categoria: "",
  nomeDenunciante: "",
  contatoDenunciante: "",
  anonima: false,
};

export default function Landing({ modern = false }) {
  const navigate = useNavigate();
  const s = modern ? modernStyles : styles;
  const [menuOpen, setMenuOpen] = useState(false);
  const [denunciaForm, setDenunciaForm] = useState(EMPTY_DENUNCIA);
  const [denunciaSending, setDenunciaSending] = useState(false);
  const [denunciaFeedback, setDenunciaFeedback] = useState(null);

  const handleNavClick = (sectionId) => {
    setMenuOpen(false);
    scrollToSection(sectionId);
  };

  const handleDenunciaChange = (event) => {
    const { name, value, type, checked } = event.target;
    setDenunciaForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDenunciaSubmit = async (event) => {
    event.preventDefault();
    setDenunciaSending(true);
    setDenunciaFeedback(null);

    try {
      await denunciaService.createPublic({
        titulo: denunciaForm.titulo.trim(),
        descricao: denunciaForm.descricao.trim(),
        categoria: denunciaForm.categoria.trim() || null,
        anonima: denunciaForm.anonima,
        nomeDenunciante: denunciaForm.anonima
          ? null
          : denunciaForm.nomeDenunciante.trim() || null,
        contatoDenunciante: denunciaForm.contatoDenunciante.trim(),
      });
      setDenunciaForm(EMPTY_DENUNCIA);
      setDenunciaFeedback({
        type: "success",
        message: "Denúncia enviada com sucesso. Obrigado pelo relato.",
      });
    } catch (error) {
      setDenunciaFeedback({
        type: "error",
        message:
          error.response?.data?.message ||
          "Não foi possível enviar a denúncia. Tente novamente.",
      });
    } finally {
      setDenunciaSending(false);
    }
  };

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      scrollToSection(hash);
    }
  }, []);

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerInner}>
          <button
            type="button"
            className={s.menuToggle}
            aria-label="Abrir menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            ☰
          </button>

          <img
            src={logo}
            alt="Senca Serviços e Engenharia"
            className={s.logo}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          />

          <div className={s.headerRight}>
            <nav className={`${s.nav} ${menuOpen ? s.navOpen : ""}`}>
              <button
                type="button"
                className={s.navLink}
                onClick={() => handleNavClick("projetos")}
              >
                Projetos
              </button>
              <button
                type="button"
                className={s.navLink}
                onClick={() => handleNavClick("sobre")}
              >
                Sobre
              </button>
              <button
                type="button"
                className={s.navLink}
                onClick={() => handleNavClick("atuacao")}
              >
                Atuação
              </button>
              <button
                type="button"
                className={s.navLink}
                onClick={() => handleNavClick("denuncia")}
              >
                Denúncia
              </button>
              <button
                type="button"
                className={s.navLink}
                onClick={() => handleNavClick("contato")}
              >
                Contato
              </button>
              <button
                type="button"
                className={`${s.systemAccess} ${s.systemAccessMobile}`}
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/login");
                }}
              >
                Acesso ao Sistema
              </button>
            </nav>

            <button
              type="button"
              className={`${s.systemAccess} ${s.systemAccessDesktop}`}
              onClick={() => navigate("/login")}
            >
              Acesso ao Sistema
            </button>
          </div>
        </div>
      </header>

      <section className={s.hero}>
        <img
          src={heroImageSrc}
          alt="Senca Serviços e Engenharia"
          className={s.heroImage}
        />
        <div className={s.heroOverlay} />
        <div className={s.heroContent}>
          {modern && (
            <span className={s.heroEyebrow}>Senca Serviços e Engenharia</span>
          )}
          <h1 className={s.heroTitle}>
            <span>Engenharia</span>
            <span>com inovação</span>
            <span>e praticidade.</span>
          </h1>
          {modern && (
            <p className={s.heroSubtitle}>
              Projetos, obras e serviços com excelência técnica para o setor
              público e privado.
            </p>
          )}
          <div className={s.heroActions}>
            <button
              type="button"
              className={s.heroButton}
              onClick={() => handleNavClick("projetos")}
            >
              Ver projetos
            </button>
            {modern && (
              <button
                type="button"
                className={s.heroButtonSecondary}
                onClick={() => navigate("/login")}
              >
                Área restrita
              </button>
            )}
          </div>
        </div>
      </section>

      <section id="projetos" className={s.projectsSection}>
        <div className={s.projectsHeader}>
          <h2 className={s.sectionTitle}>Nossos Projetos</h2>
          <div className={s.sectionDivider} />
        </div>
        <div className={s.projectsGrid}>
          {projects.map((project) => (
            <article
              key={project.title}
              className={`${s.projectCard} ${styles[project.layout]}`}
            >
              <img
                src={project.image}
                alt={project.title}
                className={s.projectImage}
              />
              <div className={s.projectOverlay}>
                <h3 className={s.projectTitle}>{project.title}</h3>
                <button
                  type="button"
                  className={s.projectLink}
                  onClick={() =>
                    project.slug
                      ? navigate(`/${project.slug}`)
                      : handleNavClick("projetos")
                  }
                >
                  Ver projetos
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="sobre" className={s.aboutSection}>
        <div className={s.aboutGrid}>
          <div className={s.aboutImageWrap}>
            <img
              src={aboutImageSrc}
              alt="Escritório Senca"
              className={s.aboutImage}
            />
          </div>
          <div className={s.aboutText}>
            <h3>Nós somos a SENCA...</h3>
            {aboutParagraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 40)}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section id="atuacao" className={s.statsSection}>
        <div className={s.statsHeader}>
          <h2 className={s.statsTitle}>Os números do sucesso</h2>
          <div className={s.statsDivider} />
        </div>

        <div className={s.statsGrid}>
          {stats.map((stat) => (
            <div key={stat.id} className={s.statCard} data-stat={stat.id}>
              <div className={s.statIcon}>
                <img
                  src={statIconImages[stat.icon]}
                  alt=""
                  className={`${s.statIconImg} ${
                    stat.icon === "tools" || stat.icon === "badge"
                      ? s.statIconImgNarrow
                      : s.statIconImgSquare
                  }`}
                />
              </div>
              <div className={s.statContent}>
                {stat.lines.map((line) => (
                  <span
                    key={line.text}
                    className={
                      line.type === "big"
                        ? s.statBig
                        : line.type === "medium"
                          ? s.statMedium
                          : line.type === "title"
                            ? s.statTitle
                            : s.statSmall
                    }
                  >
                    {line.text}
                  </span>
                ))}
                {stat.label && (
                  <span className={s.statLabel}>{stat.label}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={s.socialLinksRow}>
          {socialLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              aria-label={link.name}
              className={s.socialLink}
            >
              <img src={link.icon} alt={link.name} />
            </a>
          ))}
        </div>
      </section>

      <section id="denuncia" className={s.denunciaSection}>
        <div className={s.contactBanner}>Canal de Denúncias</div>
        <div className={s.sectionInner}>
          <p className={s.denunciaIntro}>
            Canal confidencial para relatos. O e-mail é obrigatório para
            acompanhamento do relato.
          </p>

          <form className={s.denunciaForm} onSubmit={handleDenunciaSubmit}>
            <label className={s.denunciaField}>
              <span>Título</span>
              <input
                name="titulo"
                value={denunciaForm.titulo}
                onChange={handleDenunciaChange}
                required
                maxLength={150}
                placeholder="Resumo do ocorrido"
              />
            </label>

            <label className={s.denunciaField}>
              <span>Categoria (opcional)</span>
              <select
                name="categoria"
                value={denunciaForm.categoria}
                onChange={handleDenunciaChange}
              >
                <option value="">Selecione</option>
                <option value="Assédio">Assédio</option>
                <option value="Fraude">Fraude</option>
                <option value="Segurança">Segurança</option>
                <option value="Conduta">Conduta</option>
                <option value="Outro">Outro</option>
              </select>
            </label>

            <label className={s.denunciaField}>
              <span>Descrição</span>
              <textarea
                name="descricao"
                value={denunciaForm.descricao}
                onChange={handleDenunciaChange}
                required
                rows={5}
                placeholder="Descreva o ocorrido com o máximo de detalhes possível"
              />
            </label>

            <label className={s.denunciaField}>
              <span>E-mail *</span>
              <input
                name="contatoDenunciante"
                type="email"
                value={denunciaForm.contatoDenunciante}
                onChange={handleDenunciaChange}
                required
                maxLength={120}
                placeholder="seu.email@exemplo.com"
              />
            </label>

            <label className={s.denunciaCheck}>
              <input
                type="checkbox"
                name="anonima"
                checked={denunciaForm.anonima}
                onChange={handleDenunciaChange}
              />
              <span>Não informar meu nome (relato anônimo)</span>
            </label>

            {!denunciaForm.anonima && (
              <div className={s.denunciaIdentity}>
                <label className={s.denunciaField}>
                  <span>Seu nome (opcional)</span>
                  <input
                    name="nomeDenunciante"
                    value={denunciaForm.nomeDenunciante}
                    onChange={handleDenunciaChange}
                    maxLength={120}
                  />
                </label>
              </div>
            )}

            {denunciaFeedback && (
              <p
                className={
                  denunciaFeedback.type === "success"
                    ? s.denunciaSuccess
                    : s.denunciaError
                }
              >
                {denunciaFeedback.message}
              </p>
            )}

            <button
              type="submit"
              className={s.denunciaSubmit}
              disabled={denunciaSending}
            >
              {denunciaSending ? "Enviando..." : "Enviar denúncia"}
            </button>
          </form>
        </div>
      </section>

      <section id="contato" className={s.contactSection}>
        <div className={s.contactBanner}>Contato</div>
        <div className={s.sectionInner}>
          <div className={s.contactGrid}>
            <div className={s.contactButtons}>
              <a
                href={`${contactInfo.emailHref}?subject=Solicitação de Orçamento`}
                className={s.contactButtonPrimary}
              >
                Quero um orçamento
              </a>
              <a
                href={contactInfo.phoneHref}
                className={s.contactButtonSecondary}
              >
                Falar com o time Senca
              </a>
            </div>

            <div className={s.contactDetails}>
              <h4>SENCA Comercial</h4>
              <p>Escritório</p>
              <a href={contactInfo.phoneHref}>{contactInfo.phone}</a>
              <a href={contactInfo.emailHref}>{contactInfo.email}</a>
              <p>{contactInfo.address}</p>
            </div>
          </div>
        </div>
      </section>
      <footer className={s.footer}>
        <div className={s.footerInner}>
          <img src={logo} alt="Senca" className={s.footerLogo} />
          <p>2024 - SENCA Serviços e Engenharia</p>
          <div className={s.socialLinks}>
            {socialLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                aria-label={link.name}
              >
                <img src={link.icon} alt={link.name} className={s.socialIcon} />
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
